import { MAX_LEN_WEB_S, MIN_LEN_S } from "@goalhorns/shared";

const MS_PER_S = 1000;
const MIN_LEN_MS = MIN_LEN_S * MS_PER_S;
const MAX_LEN_MS = MAX_LEN_WEB_S * MS_PER_S;

export interface ClampedRegion {
  readonly ok: true;
  readonly start_ms: number;
  readonly length_ms: number;
  readonly end_ms: number;
}

export interface RegionError {
  readonly ok: false;
  readonly reason: "song_too_short";
}

export type RegionClamp = ClampedRegion | RegionError;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Pulls a requested region inside the song. The length is snapped to whole seconds and kept
 * in [3 s, min(30 s, whole seconds of the song)]; the start is kept so the region ends inside
 * the song. A song shorter than the minimum length has no valid region.
 */
export function clampRegion(start_ms: number, length_ms: number, duration_ms: number): RegionClamp {
  if (!Number.isFinite(duration_ms) || duration_ms < MIN_LEN_MS) {
    return { ok: false, reason: "song_too_short" };
  }
  const maxLenMs = Math.min(MAX_LEN_MS, Math.floor(duration_ms / MS_PER_S) * MS_PER_S);
  const requestedLen = Number.isFinite(length_ms) ? length_ms : MIN_LEN_MS;
  const snappedLen = Math.round(requestedLen / MS_PER_S) * MS_PER_S;
  const lengthMs = clamp(snappedLen, MIN_LEN_MS, maxLenMs);

  const requestedStart = Number.isFinite(start_ms) ? Math.round(start_ms) : 0;
  const startMs = clamp(requestedStart, 0, duration_ms - lengthMs);
  return { ok: true, start_ms: startMs, length_ms: lengthMs, end_ms: startMs + lengthMs };
}

/** "m:ss" for a time in milliseconds, rounded down to whole seconds. */
export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / MS_PER_S));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** "m:ss–m:ss" for a region. */
export function formatRange(startMs: number, endMs: number): string {
  return `${formatClock(startMs)}–${formatClock(endMs)}`;
}

const NUDGE_MS = 1000;
const NUDGE_SHIFT_MS = 5000;

/**
 * Moves a region one second left or right (five with Shift); clampRegion keeps it inside the song.
 * Returns null for any other key, so the caller lets it pass.
 */
export function nudgeRegion(
  startMs: number,
  lengthMs: number,
  durationMs: number,
  key: string,
  shift: boolean,
): RegionClamp | null {
  const step = shift ? NUDGE_SHIFT_MS : NUDGE_MS;
  if (key === "ArrowLeft") {
    return clampRegion(startMs - step, lengthMs, durationMs);
  }
  if (key === "ArrowRight") {
    return clampRegion(startMs + step, lengthMs, durationMs);
  }
  return null;
}
